/**
 * **A09 · The setup-week binder cover and tabs** (Stream A, Paper.dc.html): Tuesday through
 * Thursday, a tab a day, plus crews, deliveries, the power plan and the strike. The three days are
 * Festival Ops's (`GET /ops/setup-strike`, from Year settings), the strike the Sunday after; the
 * setup channel is the radio plan's. Each day's work and hours, its deliveries, its crew and the
 * setup chair are the setup week's plan, which CIDER does not keep yet (WORKLIST ops-new): they
 * print NO DATA, never the drawing's sample.
 */
import { festivalDayWords } from './count_papers.js';
import { FestivalClock } from './festival_clock.js';
import { NO_DATA, type PaperPage } from './paper_page.js';

export interface SetupBinderInput {
  year: number;
  /** Tuesday, Wednesday and Thursday of setup week (YYYY-MM-DD); null with none set. */
  days: string[] | null;
  /** The radio plan's setup channel; null without one. */
  channel: string | null;
}

/** `2027-09-09` + 3 → `2027-09-12`. */
const plus = (ymd: string, n: number): string => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return FestivalClock.calendarDay(d);
};

/** The cover and a half-page tab for each setup day. */
export function setupBinder(i: SetupBinderInput): PaperPage[] {
  const days = i.days ?? [];
  const first = days[0];
  const last = days.at(-1);
  const strike = last ? festivalDayWords(plus(last, 3)) : null;
  const sub =
    first && last
      ? `${festivalDayWords(first)} → ${festivalDayWords(last)} · strike ${strike ?? ''} night → Monday`
      : 'No setup week in Year settings yet';
  const cover: PaperPage = {
    title: `Setup week ${String(i.year)}`,
    sub,
    big: 'Setup',
    bigSub: `Setup chair: ${NO_DATA} · Channel ${i.channel ?? NO_DATA}`,
    blocks: [
      {
        head: 'TABS',
        cols: '36px 1fr 1fr',
        rows: [
          ...days.map((d, k) => [String(k + 1), festivalDayWords(d).split(' ')[0] ?? '', NO_DATA]),
          ...[
            ['Crews', 'who, which day, who leads'],
            ['Deliveries', 'the gate sheet from the procurement calendar'],
            ['Power plan', 'circuits by tent and panel · the electrician’s page'],
            [
              'Strike',
              `${strike ? `${strike.split(' ')[0] ?? ''} night` : NO_DATA} → Monday · what goes where (Where things live)`,
            ],
          ].map(([t, w], k) => [String(days.length + k + 1), t ?? '', w ?? '']),
        ],
      },
    ],
    foot: 'Setup binder · one copy at the booth, one with the setup chair',
  };
  const tabs: PaperPage[] = days.map((d, k) => ({
    size: 'half',
    title: `Tab ${String(k + 1)} · ${festivalDayWords(d)}`,
    sub: `${NO_DATA} · the day’s work is not kept in CIDER yet`,
    blocks: [
      { head: 'DELIVERIES', cols: '70px 1fr 1fr', rows: [['', NO_DATA, '']] },
      { head: 'CREW', cols: '1fr 1fr 1fr', rows: [[NO_DATA, '', '']] },
    ],
    foot: `Setup binder · tab ${String(k + 1)}`,
  }));
  return [cover, ...tabs];
}
